import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Typography, Divider, Button, Chip, Paper, Skeleton } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useActAsClient } from "./ActAsClientContext";

// ✅ Main app auth (staff session) + portal auth (client session)
import { auth } from "../../firebase-config";
import { authPortal } from "../../firebase-portal";

const GET_COMPLETED_FORMS_URL =
  process.env.REACT_APP_GET_COMPLETED_FORMS_URL ||
  "https://getclientcompletedforms-774073159618.us-central1.run.app";

export default function ClientPortalFormsCompleted() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  const actAs = useActAsClient();
  const [params] = useSearchParams();

  const tsToLocale = (ts) =>
    ts?.toDate
      ? ts.toDate().toLocaleString()
      : ts?._seconds
      ? new Date(ts._seconds * 1000).toLocaleString()
      : ts
      ? new Date(ts).toLocaleString()
      : "Unknown";

  useEffect(() => {
    // If URL indicates act-as but context not populated yet, wait.
    const isExpectingActAs = params.has("actAsClient") || params.has("as");
    if (isExpectingActAs && !actAs.isActingAs) return;

    const run = async () => {
      setLoading(true);
      try {
        const isActingAs = !!actAs?.isActingAs;

        // ✅ Choose the correct auth source:
        // - Acting-as: use staff (main app) auth token
        // - Normal client: use portal auth token (magic-link session)
        const currentUser = isActingAs ? auth.currentUser : authPortal.currentUser;

        if (!currentUser) {
          throw new Error(isActingAs ? "No staff user" : "No portal user");
        }

        const idToken = await currentUser.getIdToken(true);

        const actAsHeaders = isActingAs ? actAs.getActAsHeaders() : {};
        const headers = { Authorization: `Bearer ${idToken}`, ...actAsHeaders };

        const { data } = await axios.post(GET_COMPLETED_FORMS_URL, {}, { headers });
        setForms(Array.isArray(data?.forms) ? data.forms : []);
      } catch (e) {
        console.error("Load completed forms failed:", e);
        setForms([]);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [actAs, params]);

  return (
    <Box>
      {/* Staff "Acting as" banner */}
      {actAs?.isActingAs && (
        <Box
          sx={{
            mb: 2,
            p: 1.25,
            borderRadius: 1,
            bgcolor: "#FFFBEB",
            border: "1px solid #FCD34D",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#92400E" }}>
            Staff preview: acting as{" "}
            <span style={{ textDecoration: "underline" }}>
              {actAs.actAsName || actAs.clientId}
            </span>
            . All actions are recorded.
          </Typography>
        </Box>
      )}

      <div className="flex items-center gap-2">
        <Typography variant="h5" sx={{ mb: 2 }}>
          Completed Forms
        </Typography>
        {loading ? (
          <Skeleton variant="rounded" width={42} height={24} />
        ) : (
          <Chip size="small" label={`${forms?.length || 0}`} color="primary" variant="outlined" />
        )}
      </div>

      <Divider sx={{ mb: 2 }} />

      {loading && <Typography variant="body2">Loading…</Typography>}

      {!loading && forms.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No completed forms.
        </Typography>
      )}

      <Box>
        {forms.map((f) => (
          <Paper
            key={f.id}
            variant="outlined"
            sx={{
              p: 2,
              mb: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div className="flex-1">
              <div className="font-semibold">{f?.title || f?.name || "Form"}</div>
              <div className="text-xs text-gray-500">
                Submitted: {tsToLocale(f?.submittedAt)}
              </div>
            </div>

            {f?.formPdfUrl && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => window.open(f.formPdfUrl, "_blank")}
              >
                View PDF
              </Button>
            )}
          </Paper>
        ))}
      </Box>
    </Box>
  );
}